// Telling two armies apart when both are wearing the same texture.
//
// The purchased set is re-materialised in palette colours — its two .blend
// files are geometrically identical and differ only in material, which is what
// lets a third army cost a colour rather than a model. A **generated** pack is
// the opposite case: one file per rank, carrying a baked map that is most of
// what makes it look like anything. Both armies share it, so something has to
// separate them.
//
// The first attempt multiplied `material.color` — side 0 white, side 1 darker.
// It works and it is crude: it darkens uniformly, so a dark texture goes muddy
// and the detail you paid for disappears exactly where it was already hardest
// to read at this camera pitch.
//
// **This rotates hue instead.** The map is sampled as authored and its colours
// are turned around the grey axis, so every bit of shading, wear and edge
// detail survives and only the palette moves. Injected with `onBeforeCompile`
// rather than by writing a material from scratch: `MeshStandardMaterial`
// already knows about this project's lighting, and a bespoke shader would be a
// second opinion about it that drifts the first time the rig changes.
//
// **And a pack that says where its paint goes gets painted instead.** A hue
// rotation has to move every pixel, because it cannot tell an armour panel
// from a copper pipe — so a red army had red copper, and separating the sides
// cost the material the model is made of. A pack may ship a coverage mask
// beside each rank (`LoadedPack.masks`), asked of the base map once, offline;
// where there is one, `armyPaint` replaces the rotation entirely, and where
// there is not the rotation stands, so the two paths coexist on one board
// rather than one waiting on the other. No shipping pack carries either — the
// base set is palette-coloured — and this is the shape the next baked-art pack
// takes.

import { type Material, type Texture, Vector3 } from "three";

import { PALETTE } from "./palette";

/**
 * Rodrigues rotation about the grey axis — the classic hue rotation.
 *
 * Around `(1,1,1)/√3` specifically, which is why it leaves greys alone: a
 * colour already on the axis is unchanged by a rotation about it. That is the
 * one real limit of this approach and it is worth stating rather than
 * discovering — a desaturated model will not separate by hue however far it is
 * turned, so `LIFT` adds a little saturation first and gives the rotation
 * something to work with.
 */
const HUE_GLSL = /* glsl */ `
uniform float armyHue;
uniform float armyLift;
uniform float armyFade;
uniform vec3 armyFadeColor;

vec3 armyRotateHue(vec3 rgb, float angle) {
  const vec3 axis = vec3(0.5773502691896258);
  float c = cos(angle);
  float s = sin(angle);
  return rgb * c + cross(axis, rgb) * s + axis * dot(axis, rgb) * (1.0 - c);
}

vec3 armyTint(vec3 rgb) {
  // Saturate a touch first, so near-grey pixels have a hue to rotate. A model
  // that is mostly stone separates by nothing at all otherwise.
  float grey = dot(rgb, vec3(0.2126, 0.7152, 0.0722));
  vec3 lifted = mix(vec3(grey), rgb, 1.0 + armyLift);
  vec3 turned = clamp(armyRotateHue(lifted, armyHue), 0.0, 1.0);
  // **And then fade toward a colour.** A hue rotation moves a texture's
  // palette without choosing where it lands — two packs turned by the same
  // angle end up somewhere different, and a desaturated one ends up nowhere —
  // so "this army is *that* colour" is unreachable however far it is turned.
  // This is the knob that gets there, and it is a *mix* rather than a multiply
  // so it can reach any colour rather than only darker.
  //
  // After the rotation, or fading to black first would leave the rotation
  // nothing to work on and the two knobs would fight.
  return mix(turned, armyFadeColor, armyFade);
}
`;

/**
 * The other path: override the texels the art nominated, leave the rest alone.
 *
 * **The mask is a proportion, not a stencil.** It says how far *this* texel
 * goes to the army's colour, so the blend is a lerp all the way to that
 * colour — a fully masked panel comes out as the army's colour and nothing
 * else, and the soft edge of a panel comes out part way. A multiply was tried
 * first and is a different thing: it scales what is already there, so a warm
 * off-white stays warm and a blue army's panels come out lilac.
 *
 * **Shaded by the texel's own brightness**, which is what keeps the override
 * from flattening a panel to a chip of flat colour. Every crease, bolt and
 * streak of wear in a white panel is a variation in brightness, so carrying
 * that into the target preserves all of it while still landing lit white
 * exactly on the palette's colour. `PAINT_WHITE` is where "lit white" is: at
 * or above it the override is total, below it the same colour darker.
 */
const PAINT_GLSL = /* glsl */ `
uniform sampler2D armyMask;
uniform vec3 armyPaintColor;
uniform float armyPaintWhite;
uniform float armyPaintStrength;

vec3 armyPaint(vec3 rgb, vec2 uv) {
  float coverage = clamp(texture2D(armyMask, uv).r * armyPaintStrength, 0.0, 1.0);
  float shade = clamp(dot(rgb, vec3(0.2126, 0.7152, 0.0722)) / armyPaintWhite, 0.0, 1.0);
  return mix(rgb, armyPaintColor * shade, coverage);
}
`;

/**
 * What army *n* reads as, taken from the palette rather than restated here.
 *
 * `PALETTE` already answers this for the purchased set, which is
 * re-materialised per side — `darkPiece` for the second army and
 * `extraPieces` for everything that arrives after it, ordered so consecutive
 * stages land far apart on the wheel. Two tables would be two answers to
 * "what colour is the third army", drifting the first time either is tuned,
 * on a board that can show both paths at once.
 *
 * Side 0 is not in it: a pack should look like itself for the player whose
 * pieces they are, so it is left exactly as authored.
 */
const ARMY_COLOR: readonly number[] = [PALETTE.darkPiece, ...PALETTE.extraPieces];

/**
 * The value every tinted army is brought to, 0..1, as its brightest channel.
 *
 * **Normalised, never picked per side.** The palette's colours are chosen to
 * separate by hue *and* value against a slate board, which is right for a flat
 * material and wrong as a fade target: `darkPiece` is a 0.24 slate, so fading
 * a textured piece into it produced the dark, muddy mass every enemy used to
 * be. Scaling each colour so its top channel reaches one number leaves the hue
 * where the palette put it and makes brightness a property of the *path*
 * rather than of the army — so seven armies differ by hue alone, which is what
 * makes them tell apart at the size a piece occupies from a 44-degree camera.
 *
 * **The fade path only**, and the painted one deliberately does not normalise
 * — see `ArmyTint.paint`.
 */
export const TINT_VALUE = 0.85;

/**
 * How far each side's hue is turned, in radians. Evenly around the circle,
 * one step per side.
 *
 * It used to be a table with one real entry (2.3, side 1) and every later army
 * falling off the end onto it. The rotation is also no longer what carries a
 * side's identity — `ARMY_COLOR` does — so it no longer has to dodge the
 * colours the board uses, and its remaining job is to keep the *variety inside
 * a texture* landing somewhere different per army. An even spread is the whole
 * of that: adjacent armies are a step apart wherever the run has got to.
 *
 * Stepped by eighths rather than sevenths so the last side is not a full turn,
 * which is no rotation at all.
 */
export const armyHue = (side: number): number => (side * Math.PI) / 4;

/** Extra saturation, so near-grey pieces still take the tint. */
const ARMY_LIFT = 0.45;

/**
 * How far a tinted side fades toward its colour, 0..1.
 *
 * The knob a hue rotation cannot supply, because a rotation about the grey
 * axis has every grey as a fixed point — "make this army *that* colour" is
 * unreachable however far it is turned.
 *
 * **Was 0.86, toward near-black.** That was the answer to one question — how
 * do you make the second army the dark one — and it is the wrong answer to the
 * question a run asks, which is how you tell six of them apart. At 0.86 the
 * pack's own map is a seventh of the result, so the baked detail that is most
 * of what a generated pack *is* goes with it. 0.6 keeps the map legible and
 * still lands the army firmly on its own colour, because what it is mixing
 * toward is now a bright one.
 */
const ARMY_FADE = 0.6;

/**
 * The brightness the artist's white sits at, above which an override is total.
 *
 * Not 1: a baked map's "white" is a lit surface rather than a pure value, and
 * the factory set's panels run 0.66 upward with their brightest faces around
 * here. Dividing by 1 instead would leave every panel a little short of its
 * own army colour, uniformly, which reads as the palette being wrong rather
 * than as shading.
 */
export const PAINT_WHITE = 0.85;

/** A palette entry as rgb, 0..1. */
function rgbOf(rgb: number): [number, number, number] {
  return [((rgb >> 16) & 0xff) / 255, ((rgb >> 8) & 0xff) / 255, (rgb & 0xff) / 255];
}

/** A palette entry normalised so its brightest channel is `TINT_VALUE`. */
function tintColor(rgb: number): [number, number, number] {
  const channels = rgbOf(rgb);
  // Scale, never clamp: clamping a channel that has already reached 1 moves
  // the hue toward white, which is the one thing this must not do — it is
  // hue that separates one army from the next.
  const top = Math.max(...channels);
  const gain = top > 0 ? TINT_VALUE / top : 0;
  return [channels[0] * gain, channels[1] * gain, channels[2] * gain];
}

/** Everything that separates one army's look from the next's. */
export interface ArmyTint {
  hue: number;
  lift: number;
  fade: number;
  /**
   * Normalised to `TINT_VALUE`, so armies differ by hue and not by value.
   *
   * Both paths land on it: it is what the rotation fades toward and what the
   * mask overrides to. One colour per army whichever way a pack is drawn, or
   * the same side reads as two different armies on a board showing both.
   */
  color: readonly [number, number, number];
  /**
   * What a masked texel is overridden to: the palette's colour **as authored**,
   * never normalised.
   *
   * The one place the two paths part company, and the reason is what each one
   * touches. A fade lands *every* texel on its colour, so a dark target makes
   * the whole piece a dark mass and the baked map goes with it — hence
   * `TINT_VALUE`, which throws away value to save the detail. An override
   * touches only the panels the art nominated and leaves the copper, the rust
   * and the lights exactly as they are, so the piece stays legible whatever
   * value the colour is. That is what puts value back on the table: a black
   * army is black, a white one white, and a yellow one is the yellow the
   * palette actually names rather than a wash of it.
   */
  paint: readonly [number, number, number];
}

/**
 * What colour army `side` paints its panels — **every side, side 0 included**.
 *
 * The fade has to leave side 0 alone: it moves every texel, so "your own army
 * as the artist drew it" and "your own army tinted" are the same choice, and a
 * pack should look like itself for the player whose pieces they are. An
 * override does not have that problem, and leaving side 0 out of it was
 * costing something real — the factory set is authored in a warm off-white, so
 * the white army sat a shade duller and yellower than the palette's own white
 * while every other army landed exactly on its colour. It reads as the light
 * being wrong rather than as a side.
 *
 * So white is painted white, like every other army is painted its own: it is
 * the only way "as authored" and "the colour this army is" can be the same
 * sentence.
 */
export function armyPaintOf(side: number): readonly [number, number, number] {
  if (side <= 0) return rgbOf(PALETTE.lightPiece);
  return rgbOf(ARMY_COLOR[(side - 1) % ARMY_COLOR.length]);
}

/**
 * How army `side` is tinted, or null for one drawn exactly as authored.
 *
 * Split out of `tintForArmy` so the thing worth asserting — that no two armies
 * land on the same colour — can be asserted without a WebGL context.
 *
 * **The fade path only.** Painting has its own answer for side 0 above.
 */
export function armyTintOf(side: number): ArmyTint | null {
  // Side 0 is untouched, so it compiles to the stock program and costs
  // nothing — the common case on a board where you are looking at your own
  // pieces most of the time.
  if (side <= 0) return null;
  return {
    hue: armyHue(side),
    lift: ARMY_LIFT,
    fade: ARMY_FADE,
    // **Wraps rather than falling off the end.** A table read past its length
    // used to hand every army after the second the same near-black, which is
    // how six camps came to look like one. Eight armies fit in three bits of
    // side and `ARMY_COLOR` is seven long, so this only ever bites on the last
    // one, and it repeats a colour rather than losing one.
    color: tintColor(ARMY_COLOR[(side - 1) % ARMY_COLOR.length]),
    paint: rgbOf(ARMY_COLOR[(side - 1) % ARMY_COLOR.length])
  };
}

/**
 * Patch a material so it tints by side, in place.
 *
 * The uniforms are held on the material rather than looked up later, because
 * `onBeforeCompile` runs once per program and the object it is handed does not
 * survive — keeping the reference is the documented way to change one after
 * compilation.
 */
export function tintForArmy(
  material: Material,
  side: number,
  mask?: Texture | null,
  /** How far this pack's mask is pushed. See `PACK_PAINT`. */
  strength = 1
): Material {
  // **A mask takes over completely rather than layering on top.** Both answer
  // the same question — how does this army read as its own — so running them
  // together would turn the copper the mask exists to protect. Asked first, so
  // the painted path covers side 0 too: `armyTintOf` answers null there and
  // would drop the player's own army out of it.
  const tint = mask ? null : armyTintOf(side);
  // Nothing to say: no mask, and the one side the fade leaves alone. The stock
  // program, which is the common case on a board where you are looking at your
  // own pieces most of the time.
  if (!mask && !tint) return material;

  const uniforms = tint
    ? {
        armyHue: { value: tint.hue },
        armyLift: { value: tint.lift },
        armyFade: { value: tint.fade },
        armyFadeColor: { value: new Vector3(...tint.color) }
      }
    : {
        armyMask: { value: mask },
        armyPaintColor: { value: new Vector3(...armyPaintOf(side)) },
        armyPaintWhite: { value: PAINT_WHITE },
        armyPaintStrength: { value: strength }
      };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${mask ? PAINT_GLSL : HUE_GLSL}`)
      // **After the map, before lighting.** Tinting the lit result would move
      // the highlights and the shadows too, which reads as a coloured lamp
      // rather than as a different army; tinting `diffuseColor` is the same
      // place the material's own `color` multiply happens.
      //
      // `vMapUv` rather than a UV of its own: a mask is a second reading of the
      // *same* map, so it has to be sampled where the map was. Guaranteed to
      // exist, since a pack with a mask is a pack with a texture.
      .replace(
        "#include <map_fragment>",
        mask
          ? "#include <map_fragment>\ndiffuseColor.rgb = armyPaint(diffuseColor.rgb, vMapUv);"
          : "#include <map_fragment>\ndiffuseColor.rgb = armyTint(diffuseColor.rgb);"
      );
  };
  // One program per *path*: within either, the source is identical and only the
  // uniforms differ, so keying by side would compile the same shader twice.
  // Keying both the same would be worse than either — the two sources differ,
  // and the cache would hand a painted material the rotation's program.
  material.customProgramCacheKey = () => (mask ? "armyPaint" : "armyTint");
  material.needsUpdate = true;
  return material;
}
